// @ts-strict-ignore
import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { SohoAlertDirective, SohoSpinboxComponent } from 'ids-enterprise-ng';
import { LMComputeBuilderService } from './services';
import * as urltemplate from 'url-template';
import { debounceTime, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {LMFieldPickerMode} from "@modal/field-picker/lm-field-picker";

@Component({
  selector: 'lm-compute-builder',
  standalone: false,
  templateUrl: './lm-compute-builder.component.html',
  styleUrls: [ './lm-compute-builder.component.scss' ]
})
export class LMComputeBuilderComponent implements OnInit, OnDestroy {

  @ViewChild(SohoSpinboxComponent) spinboxComponent: SohoSpinboxComponent;

  @ViewChild('size', { read: SohoAlertDirective }) sizeAlert: SohoAlertDirective;
  @ViewChild('decimal', { read: SohoAlertDirective }) decimalAlert: SohoAlertDirective;
  @ViewChild('fieldName', { read: SohoAlertDirective }) fieldNameAlert: SohoAlertDirective;
  @ViewChild('fieldType', { read: SohoAlertDirective }) fieldTypeAlert: SohoAlertDirective;
  @ViewChild('computePreview', { read: SohoAlertDirective }) generatedLplAlert: SohoAlertDirective;

  @Input() businessClass: string;
  @Input() includeAuditFields: boolean;
  @Input() lookupUrl: string;
  @Input() fieldSearchUrl: string;
  @Input() lplValidationUrl: string;
  @Input() isCustomCompute: boolean;
  @Input() customComputeLpl: string;
  @Input() resourceId: string;
  @Input() fieldPickerMode: LMFieldPickerMode;


  private _computeRows = new Array<LMComputeRow>();
  private _operators = new Array<string>();
  private fieldTypeAlertId = 'compute-builder-type-alert';
  private fieldNameAlertId = 'compute-builder-name-alert';
  private sizeAlertId = 'compute-builder-size-alert';
  private decimalAlertId = 'compute-builder-decimal-alert';
  private generatedLPLAlertId = 'compute-builder-lpl-alert';

  generatedComputeLpl: string;

  isSizeReadOnly = false;
  isDecimalReadOnly = false;

  private compiledValidationUrl: string;
  fieldNameValidateEventDebouncer$ = new Subject<string>();

  // Custom Compute properties
  customCompute: LMCustomCompute = {
    fieldName: '',
    fieldType: ''
  };

  fieldTypes: { value: string; label: string }[] = [
    { value: 'Date', label: Soho.Locale.translate('Date') },
    { value: 'Decimal', label: Soho.Locale.translate('DecimalLabel') },
    { value: 'Unsigned Decimal', label: Soho.Locale.translate('UnsignedDecimalLabel') },
    { value: 'Integer', label: Soho.Locale.translate('IntegerLabel') },
    { value: 'Numeric', label: Soho.Locale.translate('NumericLabel') },
    { value: 'Percent', label: Soho.Locale.translate('PercentLabel') },
    { value: 'Unsigned Percent', label: Soho.Locale.translate('UnsignedPercentLabel') },
    { value: 'Period', label: Soho.Locale.translate('Period') },
    { value: 'Time', label: Soho.Locale.translate('Time') }
  ];

  get computeRows(): Array<LMComputeRow> {
    return this._computeRows;
  }

  get operators(): Array<string> {
    return this._operators;
  }

  //attributes
  public fieldTypeDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-compute-builder-field-type' } ];
  
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private lmComputeBuilderService: LMComputeBuilderService) { }

  ngOnInit(): void {
    if (this.customComputeLpl) {
      this.customCompute = this.lmComputeBuilderService.parseCustomComputeLpl(this.customComputeLpl, this.businessClass, this.fieldSearchUrl);
      this.computeRows.push({ field: { text: '', type: 'TEXT' }, operator: '', type: 'field' });
      this.handleFieldType();
    } else {
      this.computeRows.push({ field: { text: '', type: 'TEXT' }, operator: '', type: 'field' });
    }

    this.isDecimalReadOnly = true;
    this.fieldNameValidateEventDebouncer$
      .pipe(
        filter(() => !!this.customCompute.fieldName),
        debounceTime(500))
      .subscribe(() => this.validateFieldName(this.customCompute.fieldName));
  }

  ngOnDestroy() {
    this.fieldNameValidateEventDebouncer$.complete();
  }

  onRemoveButtonEvent(event: LMComputeBuilderRemoveRowEvent, isFirst: boolean) {
    if (!isFirst) {
      this.computeRows.splice(this.computeRows.indexOf(event.computeRow), 1);
    } else {
      this.computeRows[ 0 ].field.text = '';
      this.computeRows[ 0 ].field.type = 'TEXT';
      this.computeRows[ 0 ].function = '';
      this.computeRows[ 0 ].operator = '';
      this.computeRows[ 0 ].type = 'field';
    }
    this.onComputeRowUpdate();
  }

  onAddNewRowEvent() {
    this.computeRows.push({ field: { text: '', type: 'TEXT' }, operator: '', type: 'field' });
    this.changeDetectorRef.detectChanges();
  }

  onComputeRowUpdate() {
    // if (this.isCustomCompute) {
    this.generatedComputeLpl = this.lmComputeBuilderService.convertComputeRowCriteria(this.computeRows, this.customCompute);

    if (this.generatedLplAlert) {
      this.generatedLplAlert.id = 'compute-builder-lpl-alert';
      this.generatedLplAlert.removeAllMessages();
    }

    const compiledUrlTemplate = urltemplate.parse(this.lplValidationUrl);
    this.compiledValidationUrl = compiledUrlTemplate.expand({ resourceId: this.generatedComputeLpl });

    this.lmComputeBuilderService.computeIsValid(this.compiledValidationUrl, this.businessClass, this.generatedComputeLpl, 'compute')
      .subscribe(result => {
        if (result.lplStatus && result.lplStatus !== 'LPL_VALID' && result.validationError && this.generatedLplAlert) {
          if (this.isCustomCompute) {
            this.generatedLplAlert.addInlineMessage(result.validationError.errorDescription, undefined, undefined, undefined, undefined, this.generatedLPLAlertId);
          }
        }
      });
    // }
    this.changeDetectorRef.detectChanges();
  }

  // Would not work if we add a type in the parameter.
  copyToClipboard(previewElement) {
    previewElement.textarea.element.select();
    document.execCommand('copy');
    previewElement.element.nativeElement.setSelectionRange(0, 0);
  }

  handleFieldType(value?: string) {

    this.customCompute.fieldType = value ? value : this.customCompute.fieldType;

    if (this.customCompute.fieldType === 'Date') {

      this.customCompute.size = '8';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = true;
      this.isDecimalReadOnly = true;

    } else if (this.customCompute.fieldType === 'Decimal'
      || this.customCompute.fieldType === 'Unsigned Decimal') {

      this.customCompute.size = '';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = false;
    } else if (this.customCompute.fieldType === 'Integer') {

      this.customCompute.size = '';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = true;
    } else if (this.customCompute.fieldType === 'Numeric') {

      this.customCompute.size = '';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = true;

    } else if (this.customCompute.fieldType === 'Percent'
      || this.customCompute.fieldType === 'Unsigned Percent') {

      this.customCompute.size = '';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = false;

    } else if (this.customCompute.fieldType === 'Period'
      || this.customCompute.fieldType === 'Time') {

      this.customCompute.size = '6';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = true;
      this.isDecimalReadOnly = true;
    }

    this.handleFieldChangedEvent();

    this.changeDetectorRef.markForCheck();
  }

  private validateFieldName(fieldName: string) {
    if (fieldName) {
      const result = this.lmComputeBuilderService.validateFieldName(fieldName);
      if (result !== '' && this.fieldNameAlert) {
        this.fieldNameAlert.addInlineMessage(result, undefined, undefined, undefined, undefined, this.fieldNameAlertId);
      }
    }
  }

  handleFieldNameEvent(value: string) {
    this.customCompute.fieldName = value;
    if (value) {
      this.fieldNameValidateEventDebouncer$.next('');
    }
    this.handleFieldChangedEvent();
  }

  handleSizeEvent(value: string) {
    this.customCompute.size = value;
    this.handleFieldChangedEvent();
  }

  handleDecimalEvent(value: string) {
    this.customCompute.decimal = value;
    this.handleFieldChangedEvent();
  }

  private handleFieldChangedEvent() {
    this.removeAlertMessages();
    this.generatedComputeLpl = this.lmComputeBuilderService.convertComputeRowCriteria(this.computeRows, this.customCompute);
  }

  private removeAlertMessages() {
    if (this.sizeAlert) {
      this.sizeAlert.removeAllMessages();
    }

    if (this.decimalAlert) {
      this.decimalAlert.removeAllMessages();
    }

    if (this.fieldNameAlert) {
      this.fieldNameAlert.removeAllMessages();
    }

    if (this.fieldTypeAlert) {
      this.fieldTypeAlert.removeAllMessages();
    }

    if (this.generatedLplAlert) {
      this.generatedLplAlert.removeAllMessages();
    }
  }

  handleValidationEvent() {

    this.removeAlertMessages();

    if (this.customCompute.fieldType === 'Decimal'
      || this.customCompute.fieldType === 'Unsigned Decimal'
      || this.customCompute.fieldType === 'Percent'
      || this.customCompute.fieldType === 'Unsigned Percent') {

      if (!this.customCompute.decimal) {
        if (this.decimalAlert) {
          this.decimalAlert.addInlineMessage(Soho.Locale.translate('DecimalValueRequired'), undefined, undefined, undefined, undefined, this.decimalAlertId);
        }
      }

      if (this.customCompute.decimal && this.customCompute.size
        || this.customCompute.decimal && !this.customCompute.size) {

        if (this.customCompute.size) {
          const decimal: number = +this.customCompute.decimal;
          const size: number = +this.customCompute.size;

          if (decimal > size) {
            if (this.decimalAlert) {
              this.decimalAlert.addInlineMessage(Soho.Locale.translate('DecimalValueExceeds'), undefined, undefined, undefined, undefined, this.decimalAlertId);
            }
          }
        } else {
          if (this.decimalAlert) {
            this.decimalAlert.addInlineMessage(Soho.Locale.translate('DecimalValueExceeds'), undefined, undefined, undefined, undefined, this.decimalAlertId);
          }
        }
      }
    }

    if (this.customCompute.fieldType === 'Integer') {
      if (this.customCompute.size) {
        const size: number = +this.customCompute.size;

        if (size  !== 1
          && size  !== 2
          && size  !== 4) {
          this.sizeAlert.addInlineMessage(Soho.Locale.translate('ValidIntegerSize'), undefined, undefined, undefined, undefined, this.sizeAlertId);
        }
      }
    }

    if (!this.customCompute.size && this.sizeAlert) {
      this.sizeAlert.addInlineMessage(Soho.Locale.translate('SizeRequired'), undefined, undefined, undefined, undefined, this.sizeAlertId);
    }

    if (!this.customCompute.fieldName && this.fieldNameAlert) {
      this.fieldNameAlert.addInlineMessage(Soho.Locale.translate('FieldNameRequired'), undefined, undefined, undefined, undefined, this.fieldNameAlertId);
    }

    if (!this.customCompute.fieldType && this.fieldTypeAlert) {
      this.fieldTypeAlert.addInlineMessage(Soho.Locale.translate('FieldTypeRequired'), undefined, undefined, undefined, undefined, this.fieldTypeAlertId);
    }
  }

  hasError(): boolean {

    if (this.sizeAlert.getMessage()
      || this.decimalAlert.getMessage()
      || this.fieldNameAlert.getMessage()
      || this.fieldTypeAlert.getMessage()
      || this.generatedLplAlert.getMessage()) {
      return true;
    }
  }
}

